import { useCallback, useEffect, useState } from 'react'
import { fetchMyWorkspaces, getActiveWorkspaceId, type WorkspaceSummary } from '../../lib/workspace'

/**
 * Fired on `window` after something changes the list of businesses or a
 * business name (for example a rename in Settings). Every mounted
 * `useWorkspaces` refetches when it fires.
 */
export const WORKSPACES_CHANGED_EVENT = 'hunts:workspaces-changed'

export type WorkspacesStatus = 'idle' | 'loading' | 'success' | 'error'

export interface UseWorkspacesResult {
  workspaces: WorkspaceSummary[]
  status: WorkspacesStatus
  error: string | null
  refetch: () => void
}

/**
 * Loads the businesses the signed-in user belongs to (`my_workspaces()`).
 * Never throws out of the effect: a failure, including a Supabase client
 * that cannot be built, ends in status 'error' with a message.
 */
export function useWorkspaces(): UseWorkspacesResult {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])
  const [status, setStatus] = useState<WorkspacesStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refetch = useCallback(() => setTick((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setError(null)

    void (async () => {
      try {
        const rows = await fetchMyWorkspaces()
        if (cancelled) return
        setWorkspaces(rows)
        setStatus('success')
      } catch (e) {
        if (cancelled) return
        setWorkspaces([])
        setError(e instanceof Error ? e.message : 'Could not load your businesses')
        setStatus('error')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [tick])

  useEffect(() => {
    const onChanged = () => setTick((n) => n + 1)
    window.addEventListener(WORKSPACES_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(WORKSPACES_CHANGED_EVENT, onChanged)
  }, [])

  return { workspaces, status, error, refetch }
}

/**
 * The business the app is currently using: the one the server flagged as
 * current, else the one stored in this tab, else the first in the list.
 */
export function pickCurrent(workspaces: WorkspaceSummary[]): WorkspaceSummary | null {
  const activeId = getActiveWorkspaceId()
  return (
    workspaces.find((w) => w.is_current) ??
    workspaces.find((w) => w.id === activeId) ??
    workspaces[0] ??
    null
  )
}
